// Ảnh gửi cho AI ("AI nhìn khi cần"). Nguyên tắc rẻ trước đắt sau: bản tóm DOM (chữ) là mặc định; ảnh chỉ đi kèm khi
// (1) người dùng chủ động gửi ảnh trong khung chat/hồ sơ vì không tả được bằng lời, (2) AI chẩn đoán lỗi bằng DOM trả "khong_ro".
// Ảnh là data URL JPEG/PNG/WebP đã thu nhỏ ở phía gửi (≤1024px). Proxy Boss nhận body ≤1MB → tổng ảnh mỗi lần gọi bị kẹp.
export const ANH_TOI_DA = 3; // ảnh mỗi lần gọi model
export const ANH_HO_SO_TOI_DA = 4; // ảnh trong một hồ sơ sản phẩm
export const ANH_BYTE_TOI_DA = 700_000; // tổng ký tự base64 mỗi lần gọi (≈ 520KB ảnh) — chừa chỗ cho prompt dưới 1MB của proxy
export const ANH_MOT_TAM_TOI_DA = 400_000; // một ảnh sau thu nhỏ; vượt là lỗi thu nhỏ → bỏ

const RE_DATA_URL = /^data:image\/(?:jpeg|jpg|png|webp);base64,[A-Za-z0-9+/]+=*$/;
export function laDataUrlAnh(s: unknown): s is string {
  return typeof s === "string" && s.length > 30 && s.length <= ANH_MOT_TAM_TOI_DA && RE_DATA_URL.test(s);
}

// Giữ ảnh hợp lệ theo thứ tự, tối đa `toiDa` tấm và tổng ≤ ANH_BYTE_TOI_DA (bỏ tấm cuối khi vượt). Không bao giờ ném lỗi.
export function locAnh(anh: unknown, toiDa = ANH_TOI_DA): string[] {
  if (!Array.isArray(anh)) return [];
  const out: string[] = [];
  let tong = 0;
  for (const a of anh) {
    if (!laDataUrlAnh(a)) continue;
    if (out.length >= toiDa || tong + a.length > ANH_BYTE_TOI_DA) break;
    out.push(a); tong += a.length;
  }
  return out;
}

// Nội dung tin nhắn user theo chuẩn OpenAI: chữ thuần khi không có ảnh; có ảnh → mảng [text, image_url…]
// (DeepSeek chỉ nhận image_url trong tin nhắn user; đã kiểm thật deepseek-flash 2026-09-21).
export type NoiDungUser = string | ({ type: "text"; text: string } | { type: "image_url"; image_url: { url: string } })[];
export function noiDungCoAnh(text: string, anh?: string[]): NoiDungUser {
  const ds = locAnh(anh);
  if (!ds.length) return text;
  return [{ type: "text", text: text.trim() || "(xem ảnh kèm)" }, ...ds.map((url) => ({ type: "image_url" as const, image_url: { url } }))];
}

// Ước lượng KB của một data URL để hiện cho người dùng.
export function kbCuaAnh(dataUrl: string): number {
  const i = dataUrl.indexOf(",");
  return Math.round(((dataUrl.length - (i + 1)) * 3) / 4 / 1024);
}

// ---- L-62 (vòng 2 FB 01/10): bài mà CHỮ TRỎ VÀO ẢNH ("E bị lỗi này kháng có dễ ko ae" + ảnh chụp lỗi) ----
// Trước đây chỉ gửi ảnh khi chữ < 12 từ; bài thật 17 từ nên AI KHÔNG thấy ảnh và đoán bừa "file chưa lưu kịp" (thật ra là kháng cáo kênh YouTube).
// Chữ có cụm chỉ trỏ ("lỗi này", "như này", "thế này", "trong ảnh", "this error"…) = ý chính nằm trong ảnh → cần ảnh để hiểu, dù chữ dài.
export const ANH_KHI_CHU_DUOI_TU = 12;
const BIEN = "(?:^|[\\s,.!?…:;(\"'“])";
const SAU = "(?=$|[\\s,.!?…:;)\"'”])";
const CHI_TRO = new RegExp([
  `${BIEN}(?:lỗi|cái|kiểu|chỗ|vụ|tình trạng|trường hợp|hiện tượng|thông báo|màn hình|bị)\\s+(?:này|vầy)${SAU}`,
  `${BIEN}(?:như|thế)\\s+(?:này|vầy|thế này|hình|ảnh|trên)${SAU}`,
  `${BIEN}(?:trong|ở|xem|theo)\\s+(?:ảnh|hình)${SAU}`,
  `${BIEN}(?:ảnh|hình)\\s+(?:dưới|trên|bên dưới|đính kèm|kèm theo)${SAU}`,
  "\\b(?:this|that) (?:error|issue|problem|message|thing)\\b|\\blike this\\b|\\b(?:see|in) (?:the )?(?:pic|picture|image|screenshot|photo)\\b",
].join("|"), "iu");
/** Chữ của bài/bình luận trỏ vào ảnh (ý chính nằm trong ảnh)? */
export function chuDuaVaoAnh(text: string): boolean {
  return CHI_TRO.test(String(text ?? "").normalize("NFC"));
}
/** Cần ảnh để hiểu bài: chữ quá ngắn (< 12 từ) hoặc chữ trỏ vào ảnh. Chữ rỗng → không. */
export function canAnhDeHieu(text: string): boolean {
  const n = String(text ?? "").trim().split(/\s+/).filter(Boolean).length;
  return n > 0 && (n < ANH_KHI_CHU_DUOI_TU || chuDuaVaoAnh(text));
}
